import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Timeline from './components/Timeline';
import TeamSection from './components/TeamSection';
import ContactSection from './components/ContactSection';
import ReportsSection from './components/ReportsSection';
import { useCountUp } from '@/hooks/useCountUp';

const HERO_BG = '/images/hero-bg-about.jpg';

const MISSION_IMG = '/images/about-mission-img.jpg';

function StatOverlay({ target, suffix, labelZh, labelEn, isVisible, className }: {
  target: number; suffix: string; labelZh: string; labelEn: string; isVisible: boolean; className: string;
}) {
  const { i18n } = useTranslation();
  const count = useCountUp(target, isVisible, 2000);
  const isZh = i18n.language === 'zh';
  return (
    <div className={className}>
      <div className="text-2xl font-bold tabular-nums">{count}{suffix}</div>
      <div className="text-[0.65rem] opacity-70 mt-0.5">{isZh ? labelZh : labelEn}</div>
    </div>
  );
}

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const isZh = i18n.language === 'zh';

  // Handle hash scroll (e.g. /about#contact)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [location.hash]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.4 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: 'ri-open-arm-line',
      titleKey: 'aboutPage.mission.v1.title',
      descKey: 'aboutPage.mission.v1.desc',
      color: 'bg-[#2C7DA0]/10 text-[#2C7DA0]',
    },
    {
      icon: 'ri-shake-hands-line',
      titleKey: 'aboutPage.mission.v2.title',
      descKey: 'aboutPage.mission.v2.desc',
      color: 'bg-emerald-100 text-emerald-700',
    },
    {
      icon: 'ri-lightbulb-line',
      titleKey: 'aboutPage.mission.v3.title',
      descKey: 'aboutPage.mission.v3.desc',
      color: 'bg-amber-100 text-amber-700',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-72 md:h-[480px] flex items-center overflow-hidden pt-16">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 via-[#0A2540]/70 to-[#0A2540]/40" />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(44,125,160,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(44,125,160,0.4) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/20 text-[#7EC8E3] text-xs font-semibold mb-5 tracking-widest uppercase border border-[#2C7DA0]/30">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-information-line" />
              </div>
              {t('aboutPage.hero.badge')}
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {t('aboutPage.hero.title')}
            </h1>
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              {t('aboutPage.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 md:py-28 bg-white" ref={ref}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {/* Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs font-semibold mb-5 tracking-widest uppercase">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-focus-3-line" />
                </div>
                {t('aboutPage.mission.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-5 leading-tight">
                {t('aboutPage.mission.title')}
              </h2>
              <p className="text-[#0A2540]/60 text-base leading-relaxed mb-4">
                {t('aboutPage.mission.desc1')}
              </p>
              <p className="text-[#0A2540]/60 text-base leading-relaxed">
                {t('aboutPage.mission.desc2')}
              </p>
            </div>

            {/* Image */}
            <div className="relative" ref={statsRef}>
              <div className="w-full h-72 md:h-80 rounded-2xl overflow-hidden">
                <img
                  src="/images/about-mission-img.jpg"
                  alt="GTRA Mission"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Stats overlay with countup */}
              <StatOverlay
                target={150}
                suffix="+"
                labelZh="合作机构"
                labelEn="Partners"
                isVisible={statsVisible}
                className="absolute -bottom-5 -left-5 bg-[#0A2540] rounded-xl p-4 text-white"
              />
              <StatOverlay
                target={30}
                suffix="+"
                labelZh="覆盖国家"
                labelEn="Countries"
                isVisible={statsVisible}
                className="absolute -top-5 -right-5 bg-[#2C7DA0] rounded-xl p-4 text-white"
              />
            </div>
          </div>

          {/* Core Values */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {values.map(({ icon, titleKey, descKey, color }) => (
              <div
                key={titleKey}
                className="bg-[#F7F9FC] rounded-2xl p-6 hover:-translate-y-1 transition-transform duration-200"
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${color}`}>
                  <i className={`${icon} text-xl`} />
                </div>
                <h3 className="font-bold text-[#0A2540] text-base mb-2">{t(titleKey)}</h3>
                <p className="text-[#0A2540]/60 text-sm leading-relaxed">{t(descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <Timeline />

      {/* Annual Reports & Whitepapers - placed immediately after Timeline */}
      <ReportsSection />

      {/* Team Section */}
      <TeamSection />

      {/* Contact Section */}
      <ContactSection />

      <Footer />
    </div>
  );
}
